<template>
  <div class="min-h-screen flex flex-col items-center justify-center p-5 bg-primary30">
    <div
      v-if="isMobileDevice"
      class="w-full bg-[#FEF9E7] border-[#F1C21B] p-3 text-center text-sm text-textPrimary flex justify-center items-center gap-4 fixed top-0 z-50"
    >
      <span class="text-left inter">Para médicos, acesse o Minha Escala através do aplicativo</span>
      <button @click="openApp" class="bg-primary30 text-white font-bold py-1 px-4 rounded w-40">
        Abrir app
      </button>
      <button @click="isMobileDevice = false" class="font-bold text-lg">×</button>
    </div>
    <div class="mb-12">
      <Logo width="160px" logo="full_text_white" />
    </div>

    <div class="w-full flex justify-center">
      <v-form
        @submit.prevent="submitLogin"
        :fast-fail="false"
        class="w-full max-w-md bg-white rounded shadow-lg p-10 flex flex-col items-center"
        ref="elLoginForm"
      >
        <h1 class="font-inter font-bold text-lg leading-7 text-[#333333] mb-8 text-center">
          Login
        </h1>

        <div class="w-full">
          <div data-testid="signin-email-input">
            <GenericInput
              type="email"
              label="Email"
              v-model="loginForm.values.email"
              @validate="validate('email')"
              :rules="[rules.email]"
            />
          </div>

          <div data-testid="signin-password-input">
            <PasswordInput
              v-model="loginForm.values.password"
              @validate="validate('password')"
              :rules="[rules.password]"
            />
          </div>
        </div>

        <div class="w-full flex flex-col gap-4">
          <GhostButton text="Esqueci minha senha" to="/forgot-password" width="100%" />

          <FilledButton
            text="Entrar"
            type="submit"
            width="100%"
            :disabled="isLoading"
            :loading="isLoading"
            data-testid="signin-submit-btn"
          />

          <OutlineButton
            v-if="enabledSignup"
            text="Registrar"
            to="/signup"
            width="100%"
            data-testid="signin-signup-btn"
          />
        </div>
      </v-form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive, ref, nextTick, onMounted } from 'vue'
import Logo from '@/components/designSystem/Logo/index.vue'
import PasswordInput from '@/components/designSystem/Inputs/PasswordInput.vue'
import GenericInput from '@/components/designSystem/Inputs/GenericInput.vue'
import GhostButton from '@/components/designSystem/Buttons/GhostButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import { states, LoginSchema } from '@/modules/public/Signin/schemas'
import { schemaValidateAllFields, schemaValidate } from '@/utils/yup'
import { VForm } from 'vuetify/components'
import { useAuthStore } from '@/pinia-store/useAuthStore'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { resetAllStores } from '@/plugins/piniaResetPlugin'
import { isMobileUserAgent, openAppStore } from '@/utils/appStoreLinks'
import { clearAuthStorage, consumeSessionExpiredFlag } from '@/utils/storage'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { resolvePostLoginTarget } from '@/modules/public/Signin/postLogin'

export default defineComponent({
  name: 'AppSignin',
  components: {
    Logo,
    PasswordInput,
    GhostButton,
    FilledButton,
    OutlineButton,
    GenericInput
  },
  setup() {
    const authStore = useAuthStore()
    const loginForm = reactive(states)
    const elLoginForm = ref<typeof VForm | null>(null)
    const router = useRouter()
    const toast = useToast()
    const { notifyError } = useErrorToast()
    const isLoading = ref(false)
    const isMobileDevice = ref(false)

    const permissionsStore = usePermissionsStore()
    const { flags } = useFeatureFlags()

    const detectDevice = () => {
      isMobileDevice.value = isMobileUserAgent()
    }

    const openApp = () => {
      openAppStore(() =>
        toast.info('Abra a loja de aplicativos do seu celular e procure por "Minha Escala"')
      )
    }

    onMounted(() => {
      detectDevice()

      // Flag one-shot gravada pelo interceptor 401 antes do redirect — não
      // reaparece em reloads porque consumir remove a chave do sessionStorage.
      if (consumeSessionExpiredFlag()) {
        toast.error('Sua sessão expirou. Entre novamente para continuar.')
      }
    })

    const rules = reactive({
      email: () => !loginForm.errors.email || loginForm.errors.email,
      password: () => !loginForm.errors.password || loginForm.errors.password
    })

    const validate = async (field: string) => {
      await schemaValidate(field, loginForm, LoginSchema)
    }

    const submitLogin = async () => {
      const isValidForm = await schemaValidateAllFields(loginForm, LoginSchema)
      if (isValidForm) {
        isLoading.value = true

        const userLogin = {
          email: loginForm.values.email.trim(),
          password: loginForm.values.password
        }
        try {
          resetAllStores()

          const persona = await authStore.login({
            email: userLogin.email || '',
            password: userLogin.password || ''
          })

          const target = resolvePostLoginTarget(persona, flags.enabledAntecipeAffiliate)
          if (target.kind === 'blocked') {
            clearAuthStorage()
            toast.error(target.message)
            return
          }
          if (target.fetchPermissions) await permissionsStore.fetchPermissions(true)

          router.push(target.path)
        } catch (e) {
          // Credencial errada / conta não confirmada chegam com a mensagem do backend
          // (catálogo AUTH_*); rede, timeout e 5xx viram as mensagens canônicas.
          notifyError(e, 'Não foi possível entrar. Verifique seus dados e tente novamente.')
          console.error(e)
        } finally {
          isLoading.value = false
        }
      } else {
        nextTick(async () => {
          if (elLoginForm.value) {
            await elLoginForm.value.validate()
          }
        })
      }
    }

    return {
      submitLogin,
      elLoginForm,
      loginForm,
      validate,
      rules,
      isLoading,
      isMobileDevice,
      openApp,
      enabledSignup: flags.enabledSignup
    }
  }
})
</script>
